import { Pending as NotAcceptedIcon, ThumbUp as AcceptedIcon } from "@mui/icons-material";
import {
  Alert,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  LinearProgress,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import type { FC } from "react";
import { useState } from "react";
import AsyncLoadingButton from "../components/asyncLoadingButton/AsyncLoadingButton";
import { useAuthContext } from "../auth/AuthContext";
import type { OrderConfirmationResponse } from "../orders/ordersApi";
import { useOrderConfirmation } from "./useOrderConfirmation";
import dayjs from "../dayjs";

const Status: FC<{ acceptance: OrderConfirmationResponse["acceptance"] }> = ({ acceptance }) => {
  return (
    <Stack
      direction="row"
      spacing={2}
      sx={{
        alignItems: "center",
      }}
    >
      <Typography>Status</Typography>
      {acceptance ? (
        <Chip icon={<AcceptedIcon />} label="Zaakceptowane" color="success" />
      ) : (
        <Chip icon={<NotAcceptedIcon />} label="Niezaakceptowane" color="warning" />
      )}
    </Stack>
  );
};

const StatusDetails: FC<{ acceptance: Required<OrderConfirmationResponse>["acceptance"] }> = ({ acceptance }) => (
  <Typography variant="body2">
    {dayjs(acceptance.acceptedAt).format("DD.MM.YYYY HH:mm")} przez {acceptance.acceptedBy.nameAndSurname} (
    {acceptance.acceptedBy.email})
  </Typography>
);

const ConfirmationDetails: FC<{ confirmation: OrderConfirmationResponse }> = ({ confirmation }) => (
  <Grid size={12}>
    <Stack direction="column" spacing={2}>
      <Typography variant="h5">Potwierdzenie zamówienia {confirmation.orderName}</Typography>
      <Typography>Nazwa dokumentu: {confirmation.name}</Typography>
      <Status acceptance={confirmation.acceptance} />
      {confirmation.acceptance && <StatusDetails acceptance={confirmation.acceptance} />}
    </Stack>
  </Grid>
);

const NameAndSurnameDialog: FC<{
  open: boolean;
  onConfirm: (nameAndSurname: string) => Promise<void>;
  onCancel: () => void;
}> = ({ open, onConfirm, onCancel }) => {
  const [nameAndSurname, setNameAndSurname] = useState("");
  return (
    <Dialog fullWidth open={open} onClose={onCancel}>
      <DialogTitle>Dane osoby akceptującej</DialogTitle>
      <DialogContent>
        <TextField
          label="Imię i nazwisko"
          margin="dense"
          fullWidth
          value={nameAndSurname}
          onChange={(e) => setNameAndSurname(e.target.value)}
        />
        <Typography
          variant="body2"
          sx={{
            marginTop: 1,
          }}
        >
          Niniejszym potwierdzam, iż zapoznałam/em się i akceptuję warunki niniejszego zamówienia wraz z kosztami
          anulacji. Potwierdzam równocześnie ilość i rodzaj świadczeń objętych tym zamówieniem.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button color="secondary" onClick={onCancel}>
          Anuluj
        </Button>
        <AsyncLoadingButton disabled={!nameAndSurname} onClick={() => onConfirm(nameAndSurname)}>
          Zaakceptuj
        </AsyncLoadingButton>
      </DialogActions>
    </Dialog>
  );
};

const AcceptConfirmation: FC<{ onAccept: (nameAndSurname: string) => Promise<void> }> = ({ onAccept }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button variant="outlined" color="primary" onClick={() => setOpen(true)}>
        Zaakceptuj
      </Button>
      <NameAndSurnameDialog
        open={open}
        onCancel={() => setOpen(false)}
        onConfirm={async (v) => {
          await onAccept(v);
          setOpen(false);
        }}
      />
    </>
  );
};

const OrderConfirmationPage = () => {
  const { context } = useAuthContext();
  const { confirmation, isLoading, acceptConfirmation } = useOrderConfirmation();

  if (isLoading) {
    return (
      <Grid container>
        <Grid size={12}>
          <LinearProgress data-testid="loading-indicator" />
        </Grid>
      </Grid>
    );
  }

  if (!confirmation) {
    return (
      <Grid
        container
        sx={{
          marginTop: 2,
          marginBottom: 2,
        }}
      >
        <Typography variant="h5">Takie potwierdzenie nie istnieje</Typography>
      </Grid>
    );
  }

  const isCustomer = context?.role === "CUSTOMER";
  if (!isCustomer || confirmation.acceptance) {
    return (
      <Grid
        container
        spacing={2}
        sx={{
          marginTop: 2,
          paddingBottom: 2,
        }}
      >
        <ConfirmationDetails confirmation={confirmation} />
      </Grid>
    );
  }
  const disclaimer = `Klikając "Zaakceptuj", potwierdzam zamówienie "${confirmation.orderName}" oraz akceptuje warunki płatności i anulacji.`;
  return (
    <Grid
      container
      spacing={2}
      sx={{
        marginTop: 2,
        paddingBottom: 2,
      }}
    >
      <ConfirmationDetails confirmation={confirmation} />
      <Grid size={12}>
        <Alert severity="info">{disclaimer}</Alert>
      </Grid>
      <Grid
        size={12}
        container
        sx={{
          justifyContent: "flex-start",
        }}
      >
        <Stack direction="row" spacing={2}>
          <AcceptConfirmation
            onAccept={async (v) => {
              await acceptConfirmation(v);
            }}
          />
        </Stack>
      </Grid>
    </Grid>
  );
};

export default OrderConfirmationPage;
